import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Plane, Quaternion, Vector3, Vector3Tuple } from 'three';

import { GroupProps, useThree } from '@react-three/fiber';
import { useEventCallback } from '@spazious/react-hooks';
import { useDrag } from '@use-gesture/react';

import { CanvasLabel } from '../CanvasLabel/index.js';

export type ArrowUpdatePayload = {
  start: Vector3Tuple;
  end: Vector3Tuple;
  type: 'start' | 'end' | 'move';
  first: boolean;
  last: boolean;
};

export interface ArrowProps extends Omit<GroupProps, 'onUpdate'> {
  start: Vector3Tuple; // [x, y, z]
  end: Vector3Tuple; // [x, y, z]
  color?: string;
  label?: string;
  lineWidth?: number;
  disabled?: boolean;
  onUpdate?: (payload: ArrowUpdatePayload) => void;
}

export const Arrow = forwardRef<Group, ArrowProps>(
  (
    { start, end, color = 'red', lineWidth = 2, disabled = false, onUpdate, label, ...restProps },
    ref
  ) => {
    const { pointer, raycaster, controls, camera } = useThree();
    const [canMove, setCanMove] = useState(false);
    const [canResize, setCanResize] = useState(false);

    useEffect(() => {
      document.body.style.cursor = canMove ? 'move' : canResize ? 'crosshair' : 'auto';
      return () => void (document.body.style.cursor = 'auto');
    }, [canMove, canResize]);

    const direction = useMemo(
      () => new Vector3().set(end[0] - start[0], end[1] - start[1], end[2] - start[2]),
      [start, end]
    );
    const length = direction.length();

    const midPoint = useMemo(() => {
      return new Vector3().set(
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
        (start[2] + end[2]) / 2
      );
    }, [start, end]);

    const quaternion = useMemo(() => {
      const axis = new Vector3(0, 1, 0); // cylinder default up
      return new Quaternion().setFromUnitVectors(axis, direction.clone().normalize());
    }, [direction]);

    const startRef = useRef(new Vector3());
    const endRef = useRef(new Vector3());
    const movRef = useRef(new Vector3());

    const dragHandler = useEventCallback(
      (
        { event, down, first, last }: { event: any; down: boolean; first: boolean; last: boolean },
        type: 'start' | 'end' | 'move'
      ) => {
        if (disabled) {
          return;
        }

        (event as any).stopPropagation();
        if (controls) {
          (controls as any).enabled = !down;
        }

        const plane = new Plane(new Vector3(0, 1, 0), 0);
        const projected = new Vector3();
        raycaster.setFromCamera(pointer, camera);
        raycaster.ray.intersectPlane(plane, projected);

        if (first) {
          startRef.current.set(...start);
          endRef.current.set(...end);
          movRef.current.copy(projected);
        } else {
          const dx = projected.x - movRef.current.x;
          const dz = projected.z - movRef.current.z;

          if (Math.abs(dx) < 0.01 && Math.abs(dz) < 0.01) {
            return;
          }

          const payload: ArrowUpdatePayload = {
            type,
            start,
            end,
            first,
            last,
          };
          if (type === 'move' || type === 'start') {
            payload.start = [startRef.current.x + dx, startRef.current.y, startRef.current.z + dz];
          }

          if (type === 'move' || type === 'end') {
            payload.end = [endRef.current.x + dx, endRef.current.y, endRef.current.z + dz];
          }

          onUpdate?.(payload);
        }
      }
    );

    // Drag handlers
    const startDragBind = useDrag(({ event, down, first, last }) => {
      dragHandler({ event, down, first, last }, 'start');
    });

    const endDragBind = useDrag(({ event, down, first, last }) => {
      dragHandler({ event, down, first, last }, 'end');
    });

    const moveDragBind = useDrag(({ event, down, first, last }) => {
      dragHandler({ event, down, first, last }, 'move');
    });

    const coneHeight = lineWidth * 0.06;
    const coneOffset = useMemo(() => {
      const dir = direction.clone().normalize();
      return dir.multiplyScalar(-coneHeight / 2);
    }, [direction, coneHeight]);

    return (
      <group ref={ref} {...restProps}>
        {/* Arrow line */}
        <mesh
          key="ArrowLine"
          position={[midPoint.x, midPoint.y, midPoint.z]}
          quaternion={quaternion}
          onPointerOut={() => setCanMove(false)}
          onPointerOver={() => setCanMove(true)}
          {...(moveDragBind() as any)}
        >
          <cylinderGeometry args={[lineWidth * 0.01, lineWidth * 0.01, length - coneHeight, 16]} />
          <meshBasicMaterial color={color} depthTest={false} />
        </mesh>

        {/* Arrow head */}
        <mesh
          key="ArrowHead"
          position={new Vector3(end[0], end[1], end[2]).add(coneOffset)}
          quaternion={quaternion}
          onPointerOut={() => setCanResize(false)}
          onPointerOver={(e) => {
            e.stopPropagation();
            setCanResize(true);
          }}
          {...(endDragBind() as any)}
        >
          <coneGeometry args={[lineWidth * 0.02, coneHeight, 16]} />
          <meshBasicMaterial color={color} depthTest={false} />
        </mesh>

        {/* Arrow base */}
        <mesh
          key="ArrowBase"
          position={[start[0], start[1], start[2]]}
          onPointerOut={() => setCanResize(false)}
          onPointerOver={(e) => {
            e.stopPropagation();
            setCanResize(true);
          }}
          {...(startDragBind() as any)}
        >
          <sphereGeometry args={[lineWidth * 0.05, 16, 16]} />
          <meshBasicMaterial color={color} depthTest={false} />
        </mesh>

        {/* Arrow label */}
        {label?.length && false && (
          <CanvasLabel
            key="ArrowLabel"
            bgColor="black"
            billboard={true}
            color="white"
            position={new Vector3().lerpVectors(
              new Vector3(start[0], start[1], start[2]),
              new Vector3(end[0], end[1], end[2]),
              0.5
            )}
            rotation={[-Math.PI / 2, 0, 0]}
            size={[1.8, 1]}
          >
            {label}
          </CanvasLabel>
        )}
      </group>
    );
  }
);
